"use client";

/**
 * Enrollment-preview lifecycle hook.
 *
 * Centralises every Orval-generated preview mutation under one symbol so
 * the preview Sheet, the Configure modal, and any V2 admin tooling all
 * speak the same shape:
 *
 *   - `create`   → `POST /api/.../enroll/preview`     (Configure → Preview)
 *   - `get`      → `GET  /api/.../enroll/preview/{id}` (Preview Sheet)
 *   - `patch`    → `PATCH /api/.../enroll/preview/{id}` (per-candidate
 *                  overrides + candidate removal)
 *   - `commit`   → `POST /api/.../commit`             (Enroll button)
 *   - `discard`  → `DELETE /api/.../enroll/preview/{id}` (Cancel /
 *                  Esc-with-overrides)
 *
 * Two consumption shapes are exposed:
 *
 *   - `useEnrollmentPreview()` — the plain mutation grab-bag for callers
 *     that only need the create flow (Configure modal). Backwards-
 *     compatible with the Phase H surface.
 *   - `useEnrollmentPreview({ jobId, sequenceId, previewSessionId })` —
 *     binds the get query and pre-binds the mutation params so call
 *     sites in the Preview Sheet don't have to re-pass {jobId,
 *     sequenceId, previewSessionId} on every keystroke.
 *
 * Refs:
 *   - spec §6 (preview view)
 *   - spec §10 (preview lifecycle endpoints)
 *   - spec §13.1 (preview lifecycle hook contract)
 */

import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type {
  EnrollmentCommitRequest,
  EnrollmentPreviewPatchRequest,
  EnrollmentPreviewResponse,
} from "../lib/types";
import {
  getGetPreviewQueryKey,
  useCommitPreview,
  useCreatePreview,
  useDiscardPreview,
  useGetPreview,
  usePatchPreview,
} from "@/lib/api/generated";

export interface BoundPreviewArgs {
  /** Job UUID — present in every preview URL. */
  jobId?: string;
  /** Sequence UUID — present in every preview URL. */
  sequenceId?: string;
  /**
   * Preview session id from the route (`previewSessionId`). Hooks gate
   * their `enabled` flag on this so the preview Sheet can mount the
   * hook before the route param resolves without firing a 404.
   */
  previewSessionId?: string;
  /**
   * When false the get query is suspended even if the ids are present.
   * Useful for e.g. the discard-confirm modal which doesn't need a
   * fresh fetch.
   */
  enabled?: boolean;
}

export function useEnrollmentPreview(bound: BoundPreviewArgs = {}) {
  const { jobId, sequenceId, previewSessionId, enabled = true } = bound;

  const queryClient = useQueryClient();
  const create = useCreatePreview();
  const patch = usePatchPreview();
  const commit = useCommitPreview();
  const discard = useDiscardPreview();

  // Only enable the get query when every id is hydrated. The Orval-
  // generated query already short-circuits on falsy ids; we re-assert
  // here for the `enabled: false` opt-out.
  const get = useGetPreview(
    jobId ?? "",
    sequenceId ?? "",
    previewSessionId ?? "",
    {
      query: {
        enabled: Boolean(enabled && jobId && sequenceId && previewSessionId),
      },
    },
  );

  // Monotonic PATCH sequence shared across EVERY call site for this
  // preview (all per-step override autosaves + candidate removals). The
  // backend drops any PATCH whose `request_seq` is behind the session's
  // latest, so independent `useDebouncedOverride` instances (one per step
  // card) MUST NOT each start their own counter from 1 — a second card's
  // first save would otherwise be silently no-op'd. Stamping the seq here
  // guarantees a single strictly-increasing stream, and lets us ignore
  // stale (out-of-order) responses when writing the cache.
  const patchSeqRef = useRef(0);
  const patchAppliedSeqRef = useRef(0);

  /**
   * Convenience wrapper around `patch.mutateAsync` that inlines the
   * bound preview ids (so call sites in the preview Sheet only pass
   * the body). Throws if the hook was constructed without ids — this
   * is a programmer error, not a recoverable runtime case.
   */
  const patchPreview = useCallback(
    async (data: EnrollmentPreviewPatchRequest) => {
      if (!jobId || !sequenceId || !previewSessionId) {
        throw new Error(
          "useEnrollmentPreview.patchPreview() called without bound jobId/sequenceId/previewSessionId.",
        );
      }
      const seq = ++patchSeqRef.current;
      const result = await patch.mutateAsync({
        jobId,
        sequenceId,
        previewSessionId,
        // Override any caller-supplied seq with the centralized one so the
        // backend never wrongly drops a concurrent autosave from a
        // different step card.
        data: { ...data, request_seq: seq },
      });
      // The PATCH response IS the freshly re-validated preview (overrides
      // applied, issues recomputed, candidates re-bucketed). Write it straight
      // into the get-query cache so the UI reflects it immediately — e.g.
      // picking an email override clears `contact_email_missing` and moves the
      // recipient out of the Fix bucket. Without this the mutation result was
      // dropped and the recipient stayed stuck in the issues list.
      //
      // Only the latest-issued response may write the cache: a slower,
      // earlier PATCH resolving after a newer one must not clobber the
      // newer state.
      if (result && seq >= patchAppliedSeqRef.current) {
        patchAppliedSeqRef.current = seq;
        queryClient.setQueryData(
          getGetPreviewQueryKey(jobId, sequenceId, previewSessionId),
          result as EnrollmentPreviewResponse,
        );
      }
      return result;
    },
    [jobId, sequenceId, previewSessionId, patch, queryClient],
  );

  const commitPreview = useCallback(
    async (data?: EnrollmentCommitRequest) => {
      if (!jobId || !sequenceId || !previewSessionId) {
        throw new Error(
          "useEnrollmentPreview.commitPreview() called without bound jobId/sequenceId/previewSessionId.",
        );
      }
      return commit.mutateAsync({
        jobId,
        sequenceId,
        previewSessionId,
        data,
      });
    },
    [jobId, sequenceId, previewSessionId, commit],
  );

  const discardPreview = useCallback(async () => {
    if (!jobId || !sequenceId || !previewSessionId) {
      throw new Error(
        "useEnrollmentPreview.discardPreview() called without bound jobId/sequenceId/previewSessionId.",
      );
    }
    return discard.mutateAsync({
      jobId,
      sequenceId,
      previewSessionId,
    });
  }, [jobId, sequenceId, previewSessionId, discard]);

  return {
    create,
    get,
    patch,
    commit,
    discard,
    patchPreview,
    commitPreview,
    discardPreview,
  };
}
